/**
 * Transition Rules Registry
 *
 * Central registry of all available version transitions.
 * Supports EnergyPlus versions from 9.0 to 26.1.
 */

import type { VersionTransition } from '../types';
import { parseVersion, compareVersions } from '../types';

// Import all transition creators
import { createTransition_9_0_to_9_1 } from './v9_0_to_v9_1';
import { createTransition_9_1_to_9_2 } from './v9_1_to_v9_2';
import { createTransition_9_2_to_9_3 } from './v9_2_to_v9_3';
import { createTransition_9_3_to_9_4 } from './v9_3_to_v9_4';
import { createTransition_9_4_to_9_5 } from './v9_4_to_v9_5';
import { createTransition_9_5_to_9_6 } from './v9_5_to_v9_6';
import { createTransition_9_6_to_22_1 } from './v9_6_to_v22_1';
import { createTransition_22_1_to_22_2 } from './v22_1_to_v22_2';
import { createTransition_22_2_to_23_1 } from './v22_2_to_v23_1';
import { createTransition_23_1_to_23_2 } from './v23_1_to_v23_2';
import { createTransition_23_2_to_24_1 } from './v23_2_to_v24_1';
import { createTransition_24_1_to_24_2 } from './v24_1_to_v24_2';
import { createTransition_24_2_to_25_1 } from './v24_2_to_v25_1';
import { createTransition_25_1_to_25_2 } from './v25_1_to_v25_2';
import { createTransition_25_2_to_26_1 } from './v25_2_to_v26_1';

// Re-export individual transition creators
export { createTransition_9_0_to_9_1 } from './v9_0_to_v9_1';
export { createTransition_9_1_to_9_2 } from './v9_1_to_v9_2';
export { createTransition_9_2_to_9_3 } from './v9_2_to_v9_3';
export { createTransition_9_3_to_9_4 } from './v9_3_to_v9_4';
export { createTransition_9_4_to_9_5 } from './v9_4_to_v9_5';
export { createTransition_9_5_to_9_6 } from './v9_5_to_v9_6';
export { createTransition_9_6_to_22_1 } from './v9_6_to_v22_1';
export { createTransition_22_1_to_22_2 } from './v22_1_to_v22_2';
export { createTransition_22_2_to_23_1 } from './v22_2_to_v23_1';
export { createTransition_23_1_to_23_2 } from './v23_1_to_v23_2';
export { createTransition_23_2_to_24_1 } from './v23_2_to_v24_1';
export { createTransition_24_1_to_24_2 } from './v24_1_to_v24_2';
export { createTransition_24_2_to_25_1 } from './v24_2_to_v25_1';
export { createTransition_25_1_to_25_2 } from './v25_1_to_v25_2';
export { createTransition_25_2_to_26_1 } from './v25_2_to_v26_1';

/**
 * All available transitions indexed by "fromMajor.fromMinor"
 */
export function getAllTransitions(): Map<string, VersionTransition> {
  const transitions = new Map<string, VersionTransition>();

  // Add all transitions in order
  transitions.set('9.0', createTransition_9_0_to_9_1());
  transitions.set('9.1', createTransition_9_1_to_9_2());
  transitions.set('9.2', createTransition_9_2_to_9_3());
  transitions.set('9.3', createTransition_9_3_to_9_4());
  transitions.set('9.4', createTransition_9_4_to_9_5());
  transitions.set('9.5', createTransition_9_5_to_9_6());
  transitions.set('9.6', createTransition_9_6_to_22_1());
  transitions.set('22.1', createTransition_22_1_to_22_2());
  transitions.set('22.2', createTransition_22_2_to_23_1());
  transitions.set('23.1', createTransition_23_1_to_23_2());
  transitions.set('23.2', createTransition_23_2_to_24_1());
  transitions.set('24.1', createTransition_24_1_to_24_2());
  transitions.set('24.2', createTransition_24_2_to_25_1());
  transitions.set('25.1', createTransition_25_1_to_25_2());
  transitions.set('25.2', createTransition_25_2_to_26_1());

  return transitions;
}

/**
 * Get the transition for a specific source version
 */
export function getTransition(fromVersion: string): VersionTransition | undefined {
  const transitions = getAllTransitions();
  return transitions.get(fromVersion);
}

/**
 * Get the chain of transitions needed to go from one version to another
 */
export function getTransitionChain(
  fromVersion: string,
  toVersion: string
): VersionTransition[] {
  const transitions = getAllTransitions();
  const chain: VersionTransition[] = [];

  const fromParsed = parseVersion(fromVersion);
  const toParsed = parseVersion(toVersion);

  // Validate we're upgrading, not downgrading
  if (compareVersions(fromParsed, toParsed) >= 0) {
    return []; // Already at or past target version
  }

  let currentVersion = fromVersion;
  let iterations = 0;
  const maxIterations = 20; // Safety limit

  while (iterations < maxIterations) {
    const transition = transitions.get(currentVersion);
    if (!transition) {
      // No transition available from current version
      break;
    }

    chain.push(transition);

    // Check if we've reached or passed the target
    if (compareVersions(transition.toVersion, toParsed) >= 0) {
      break;
    }

    // Move to next version
    currentVersion = `${String(transition.toVersion.major)}.${String(transition.toVersion.minor)}`;
    iterations++;
  }

  return chain;
}

/**
 * Get all supported source versions
 */
export function getSupportedSourceVersions(): string[] {
  return Array.from(getAllTransitions().keys()).sort((a, b) => {
    const vA = parseVersion(a);
    const vB = parseVersion(b);
    return compareVersions(vA, vB);
  });
}

/**
 * Get the latest supported target version
 */
export function getLatestTargetVersion(): string {
  const transitions = getAllTransitions();
  let latest = { major: 0, minor: 0 };

  for (const transition of transitions.values()) {
    if (compareVersions(transition.toVersion, latest) > 0) {
      latest = transition.toVersion;
    }
  }

  return `${String(latest.major)}.${String(latest.minor)}`;
}

/**
 * Check if a transition path exists from one version to another
 */
export function canTransition(fromVersion: string, toVersion: string): boolean {
  const chain = getTransitionChain(fromVersion, toVersion);
  if (chain.length === 0) {
    return false;
  }

  // Verify the chain ends at or past the target
  const lastTransition = chain[chain.length - 1];
  if (!lastTransition) {
    return false;
  }
  const toParsed = parseVersion(toVersion);
  return compareVersions(lastTransition.toVersion, toParsed) >= 0;
}

/**
 * Get the earliest supported source version
 */
export function getEarliestSourceVersion(): string {
  const versions = getSupportedSourceVersions();
  return versions[0] || '';
}
